import { createContext, useContext } from "react";

/**
 * O QUE A SESSÃO PODE FAZER.
 *
 * Vem de duas chamadas feitas UMA vez no App (`/api/me` e `/api/permissoes/me`)
 * e é consumido lá embaixo, nas telas — sem carregar prop por prop.
 *
 * `diagnostico` e `liberacaoCliente` NÃO são dadas a todo usuário cadastrado:
 * quem interpreta e assina, e quem solta o resultado para o cliente, é decisão
 * de poucas pessoas (ver server/permissoes.ts). A tela esconde o que a pessoa
 * não pode fazer, e o servidor recusa de qualquer jeito — tela é conforto,
 * regra é servidor.
 */
export interface PermissoesSessao {
  /** Só administradores excluem registros (regra do Portal). */
  podeExcluir: boolean;
  /** Preencher, finalizar e reabrir o diagnóstico das amostras. */
  diagnostico: boolean;
  /** Liberar os resultados de uma OS para o cliente. */
  liberacaoCliente: boolean;
  /**
   * As abas que esta pessoa pode ver.
   *
   * VAZIO quer dizer SEM RESTRIÇÃO (vê todas), e não "não vê nenhuma" — é o
   * contrário das duas permissões acima, de propósito: restringir abas é um
   * ato deliberado de quem administra, não o estado inicial de todo mundo.
   * A mesma regra vale no servidor (ver server/permissoes.ts).
   */
  abas: string[];
}

const VAZIO: PermissoesSessao = {
  podeExcluir: false,
  diagnostico: false,
  liberacaoCliente: false,
  abas: [],
};

const Ctx = createContext<PermissoesSessao>(VAZIO);
export const PermissoesProvider = Ctx.Provider;

/** Tudo o que a sessão pode fazer. */
export const usePermissoes = (): PermissoesSessao => useContext(Ctx);

/** Só administradores podem excluir registros (regra do Portal). */
export const usePodeExcluir = (): boolean => useContext(Ctx).podeExcluir;

/**
 * Esta aba aparece para a sessão?
 *
 * Sem nenhuma aba marcada, todas aparecem (ver `abas` acima). O menu usa isto
 * para montar a lista; o servidor recusa do mesmo jeito quem tentar pela URL.
 */
export function usePodeVerAba(): (key: string) => boolean {
  const { abas } = useContext(Ctx);
  return (key: string) => abas.length === 0 || abas.includes(key);
}
